import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FORMLY_CONFIG,
  FormlyFieldConfig,
  FormlyForm,
  FormlyModule,
} from '@ngx-formly/core';
import { uniq } from 'lodash';
import { NgxCurrencyDirective } from 'ngx-currency';

import { MaterialModule } from '@lockton-apps/material';
import { UiModule } from '@lockton-apps/shared/ui/lui';

import { AddonsWrapperModule } from './addons-wrapper/addons-wrapper.module';
import { AddressTypeModule } from './address-type/address-type.module';
import { AlertTypeModule } from './alert-type/alert-type.module';
import { AutocompleteTypeModule } from './autocomplete-type/autocomplete-type.module';
import { CalcTableTypeModule } from './calc-table-type/calc-table-type.module';
import { CardTypeModule } from './card-type/card-type.module';
import { CheckboxTypeModule } from './checkbox-type/checkbox-type.module';
import { ChipsAutocompleteTypeModule } from './chips-autocomplete/chips-autocomplete-type.module';
import { ConfirmationBoxTypeModule } from './confirmation-box-type/confirmation-box-type.module';
import {
  customValidator, fieldArrayValidation,
  fieldMatchValidator,
  minLengthLowercase,
  minLengthNumeric,
  minLengthSpecial,
  minLengthUppercase,
  minOrNull,
  noSpaces
} from './custom-validators';
import { DateRangePickerTypeModule } from './date-range-picker-type/date-range-picker-type.module';
import { DatepickerTypeModule } from './datepicker-type/datepicker-type.module';
import { DefaultWrapperModule } from './default-wrapper/default-wrapper.module';
import { DividerTypeModule } from './divider-type/divider-type.module';
import { DynamicFieldTypeModule } from './dynamic-field-type/dynamic-field-type.module';
import { DynamicFormCalcTableCellModule } from './dynamic-form-calc-table-cell/dynamic-form-card.module';
import { DynamicFormCalcTableModule } from './dynamic-form-calc-table/dynamic-form-calc-table.module';
import { DynamicFormCardModule } from './dynamic-form-card/dynamic-form-card.module';
import { DynamicFormCellModule } from './dynamic-form-cell/dynamic-form-cell.module';
import { DynamicFormTableModule } from './dynamic-form-table/dynamic-form-table.module';
import { DynamicTablesModule } from './dynamic-tables-type/dynamic-tables-type.module';
import { ExternalTypeModule } from './external-type/external-type.module';
import { FormFieldWrapperModule } from './form-field-wrapper/form-field-wrapper.module';
import { HiddenTypeModule } from './hidden-type/hidden-type.modul';
import { ImageFileInputModule } from './image-file-input-type/image-file-input.module';
import { InfoPanelTypeModule } from './info-panel-type/info-panel-type.module';
import { InputFieldTypeModule } from './input-field-type/input-field-type.module';
import { MatFormFieldWrapperModule } from './mat-form-field-wrapper/mat-form-field-wrapper.module';
import { MultiCheckboxTypeModule } from './multi-checkbox-type/multi-checkbox-type.module';
import { PanelWrapperModule } from './panel-wrapper/panel-wrapper.module';
import { PasswordFieldTypeModule } from './password-field-type/password-field-type.module';
import { SelectBoxTypeModule } from './select-box-type/select-box-type.module';
import { SelectFieldTypeModule } from './select-field-type/select-field-type.module';
import { DynamicFormBuilderService } from './services/dynamic-form-builder.service';
import { SimpleTableTypeModule } from './simple-table-type/simple-table-type.module';
import { SlideInOutWrapperModule } from './slide-in-out-wrapper/slide-in-out-wrapper.module';
import { SummaryTypeModule } from './summary-type/summary-type.module';
import { TemplateTypeModule } from './template-type/template-type.module';
import { TextareaFieldTypeModule } from './textarea-field-type/textarea-field-type.module';
import { YearpickerTypeModule } from './yearpicker-type/yearpicker-type.module';
import { ArrayTypeModule } from './array-type/array-type.module';
import { SignaturePadTypeModule } from './signature-pad-type/signature-pad-type.module';
import { ReverseCalcTableTypeModule } from './reverse-calc-table-type/reverse-calc-table-type.module';
import { PipesModule } from '@lockton-apps/shared/ui/pipes';
import { RadioTypeModule } from './radio-type/radio-type.module';
import { FileInputTypeModule } from './file-input-type/file-input-type.module';
import { dynamicFormsLocaleFactory } from './providers/config';
import { LOCALE_CONFIG } from '@lockton-apps/shared/util/localization';
import {
  CobMetaService,
  registerCobMetaOptionsPipeExtension,
} from '@lockton-apps/shared/util/constants';

export function wrappersExtension(field: FormlyFieldConfig) {
  if (!field.props) {
    return;
  }

  field.wrappers?.unshift('default');

  if (field.props['addonLeft'] || field.props['addonRight']) {
    field.wrappers?.push('addons');
  }

  field.wrappers = uniq(field.wrappers);
}

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    UiModule,
    FormlyModule.forRoot({
      extensions: [
        { name: 'wrappers', extension: { onPopulate: wrappersExtension } },
      ],
      validators: [
        {
          name: 'lowercaseChars',
          validation: minLengthLowercase,
        },
        {
          name: 'uppercaseChars',
          validation: minLengthUppercase,
        },
        {
          name: 'numberChars',
          validation: minLengthNumeric,
        },
        {
          name: 'specialChars',
          validation: minLengthSpecial,
        },
        {
          name: 'noSpaces',
          validation: noSpaces,
        },
        {
          name: 'email',
          validation: Validators.email,
        },
        {
          name: 'requiredTrue',
          validation: Validators.requiredTrue,
        },
        {
          name: 'fieldMatch',
          validation: fieldMatchValidator,
          options: { paths: ['password', 'confirmPassword'] },
        },
        {
          name: 'customValidator',
          validation: customValidator,
        },
        {
          name: 'minOrNull',
          validation: minOrNull,
        },
        {
          name: 'fieldArrayValidation',
          validation: fieldArrayValidation,
        },
      ],
      validationMessages: [
        { name: 'required', message: $localize`This field is required` },
        {
          name: 'requiredTrue',
          message: $localize`Please confirm you have read and agree to the above statement`,
        },
        { name: 'fieldMatch', message: $localize`Fields dont match` },
        { name: 'email', message: $localize`Please enter a valid email` },
        { name: 'lt', message: $localize`Values exceed limit` },
        { name: 'gt', message: $localize`Values must exceed limit` },
      ],
    }),
    AutocompleteTypeModule,
    AlertTypeModule,
    /** Still in development do not use  */
    ArrayTypeModule,
    ChipsAutocompleteTypeModule,
    DatepickerTypeModule,
    DividerTypeModule,
    MatFormFieldWrapperModule,
    InputFieldTypeModule,
    SelectFieldTypeModule,
    CheckboxTypeModule,
    MultiCheckboxTypeModule,
    DateRangePickerTypeModule,
    AddonsWrapperModule,
    PanelWrapperModule,
    SelectBoxTypeModule,
    ImageFileInputModule,
    FileInputTypeModule,
    FormFieldWrapperModule,
    DefaultWrapperModule,
    DynamicFieldTypeModule,
    AddressTypeModule,
    CalcTableTypeModule,
    TextareaFieldTypeModule,
    ConfirmationBoxTypeModule,
    DynamicTablesModule,
    TemplateTypeModule,
    DynamicFormCardModule,
    DynamicFormCellModule,
    DynamicFormTableModule,
    HiddenTypeModule,
    CardTypeModule,
    DynamicFormCalcTableModule,
    InfoPanelTypeModule,
    NgxCurrencyDirective,
    SimpleTableTypeModule,
    ExternalTypeModule,
    PasswordFieldTypeModule,
    SlideInOutWrapperModule,
    YearpickerTypeModule,
    SummaryTypeModule,
    DynamicFormCalcTableCellModule,
    SignaturePadTypeModule,
    ReverseCalcTableTypeModule,
    PipesModule,
    RadioTypeModule,
  ],
  exports: [FormlyForm],
  providers: [
    DynamicFormBuilderService,
    {
      provide: dynamicFormsLocaleFactory,
      useFactory: dynamicFormsLocaleFactory,
      deps: [LOCALE_CONFIG],
    },
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: registerCobMetaOptionsPipeExtension,
      deps: [CobMetaService],
    },
  ],
})
export class DynamicFormsModule {}
